import type { WalletRow } from "@/lib/api";

export type CashCurrency =
  | "BRL"
  | "USD"
  | "USDC"
  | "MXN"
  | "CAD"
  | "INR";

/**
 * Result of picking the user's "primary" cash wallet for chrome
 * surfaces (header pill, mobile bottom nav). Currency is narrowed to
 * the closed-beta-supported set; raw `WalletRow.currency` is `string`
 * because the schema isn't constrained at the DB layer.
 */
export interface PrimaryCashWallet {
  cents: number;
  currency: CashCurrency;
}

/**
 * Pick the user's "primary" cash wallet — the one whose balance the
 * chrome surfaces (header pill, mobile bottom nav balance cell,
 * trade panel) highlight.
 *
 * **2026-05-09 architectural fix.** The previous shape preferred a
 * wallet matching the user's `displayCurrency`, then USDC, then
 * any. That creates a divergence whenever a user holds both a BRL
 * AND a USDC wallet (real case: founder admin-topped a BRL wallet
 * after the post-STORY-1700-part-3 USDC-as-truth flip):
 *
 *   - SiteHeader: useEffect deps include `displayCurrency`, picker
 *     resolves BRL → header shows R$19.33.
 *   - TradePanel: useEffect deps were `[market.id]` only, so the
 *     picker ran once at mount with displayCurrency='USDC' default
 *     and never re-resolved → trade panel locked on USDC ($0.08 →
 *     R$0.40 in BRL display).
 *   - Two prominent surfaces showing different "cash balance" for
 *     the same user. Founder reported 2026-05-09.
 *
 * CLAUDE.md is unambiguous:
 *
 *   "BR-primary display flip — the ledger architecture stays
 *    USDC-as-truth. The change is purely presentation-layer."
 *
 * So: pick USDC unconditionally. Display layer (formatBalanceForRegion
 * / formatMoneyPrimary) handles BRL projection via the FX rate. The
 * only fallback is a legacy account that has no USDC wallet at all
 * (pre-STORY-1700 grandfathered) — pick whatever cash wallet exists
 * so we don't blank out the chrome pill. New signups always have a
 * USDC wallet (STORY-1700 part 2), so this fallback is rare-and-
 * shrinking.
 *
 * `displayCurrency` stays in the signature so existing callers don't
 * need to update; it's no longer consulted for which wallet to
 * read but is kept for type/compatibility.
 *
 * Extracted from `SiteHeader` + `MobileBottomNav` (both had this
 * same chain inline). Centralising the rule prevents the surfaces
 * from drifting apart.
 */
export function pickPrimaryCashWallet(
  wallets: WalletRow[] | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _displayCurrency: CashCurrency,
): PrimaryCashWallet | null {
  if (!wallets || wallets.length === 0) return null;
  const cashWallets = wallets.filter((w) => w.kind === "cash");
  if (cashWallets.length === 0) return null;

  // USDC is the canonical cash unit (USDC-as-truth, locked
  // 2026-05-02 v3 + reaffirmed 2026-05-08 BR-primary display flip).
  // Fall through to "first cash wallet" only for legacy accounts
  // without a USDC wallet — those should be migrated by the
  // BRL→USDC consolidation script (filed as separate work item).
  const cash =
    cashWallets.find((w) => w.currency === "USDC") ??
    cashWallets.find((w) => w.currency === "USD") ??
    cashWallets[0];

  return {
    cents: Number(cash.balance_cents),
    currency: cash.currency as CashCurrency,
  };
}
